/**
 * useAuthedImage — obrazek z trasy API, która wymaga tokenu (logo kontrahenta, logo firmy).
 *
 * `<img src>` nie wyśle nagłówka Authorization, a magazyn plików nie ma publicznego adresu
 * (na VPS słucha tylko lokalnie), więc obrazek pobieramy klientem `api` jako blob i podajemy
 * jako `blob:` URL. `path` jest względny do `/api` (np. `/contractors/<id>/logo?v=…`) — zmiana
 * parametru `v` po ponownym wgraniu pobiera świeży obrazek.
 *
 * Zwraca `null`, gdy brak ścieżki, obrazek jeszcze się ładuje albo pobranie się nie udało —
 * wywołujący pokazuje wtedy swój placeholder.
 */

import { useEffect, useState } from 'react';

import { api } from '@/lib/api';

export function useAuthedImage(path: string | null): string | null {
  const [loaded, setLoaded] = useState<{ path: string; url: string } | null>(null);

  useEffect(() => {
    if (!path) return;
    let cancelled = false;
    let objectUrl: string | null = null;
    api
      .get<Blob>(path, { responseType: 'blob' })
      .then((res) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(res.data);
        setLoaded({ path, url: objectUrl });
      })
      .catch(() => {
        /* brak logo albo błąd odczytu — placeholder */
      });
    return () => {
      cancelled = true;
      if (objectUrl) {
        // Zapomnij zwolniony URL — przy powrocie do tej samej ścieżki (A → B → A) hook nie może
        // zwrócić martwego `blob:` zanim przyjdzie ponowne pobranie.
        const revoked = objectUrl;
        setLoaded((prev) => (prev?.url === revoked ? null : prev));
        URL.revokeObjectURL(revoked);
      }
    };
  }, [path]);

  // Obrazek innej ścieżki (np. po usunięciu logo albo zmianie rekordu) nie jest pokazywany.
  return path && loaded?.path === path ? loaded.url : null;
}
